import { Grid, CircularProgress, Typography, Snackbar, Button } from '@rbx/ui';
import React, { useEffect, useCallback } from 'react';
import { useTranslation } from '@rbx/intl';
import { CreatorStreamNotificationsMarkCreatorStreamNotificationsReadOperationRequest } from '@rbx/clients/creatorNotificationStreamsApi';
import Notification from './notificationGroups/Notification';
import NotificationV2 from './notificationGroups/NotificationV2';
import BundledNotification from './notificationGroups/BundledNotification';
import { User } from './types/user';
import TNotificationGroup from './types/TNotificationGroup';
import useNotificationListStyles from './NotificationList.styles';
import { markNotificationAtIndexAsRead } from './utils/markNotificationAtIndexAsRead';
import signalREventEmitter from './contexts/SignalREventEmitter';
import { useNotificationClient } from './contexts/NotificationClientProvider';
import TrayHeader from './TrayHeader';
import FailedToLoad from './spotIllustrations/FailedToLoad';
import NoNewNotifications from './spotIllustrations/NoNewNotifications';

type TNotificationListProps = {
  failedToLoad: boolean;
  listRef: React.MutableRefObject<HTMLDivElement | null>;
  loadingTray: boolean;
  markAllAsRead: () => void;
  menuAnchor: HTMLElement | null;
  newNotificationExists: boolean;
  noMoreNotifications: boolean;
  notifications: TNotificationGroup[];
  setMenuAnchor: (el: HTMLElement | null) => void;
  setNewNotificationExists: (notificationExists: boolean) => void;
  setNotifications: React.Dispatch<React.SetStateAction<TNotificationGroup[]>>;
  user: User | null;
  loadPage: (isReload: boolean) => Promise<void>;
  setExpanded: (expanded: boolean) => void;
  onSettingsClick?: () => void;
  enableNotificationTrayV2?: boolean;
};

const NEXT_PAGE_SCROLL_THRESHOLD = 200;

const NotificationList = React.forwardRef<HTMLDivElement, TNotificationListProps>(
  (
    {
      failedToLoad,
      listRef,
      loadingTray,
      markAllAsRead,
      menuAnchor,
      newNotificationExists,
      noMoreNotifications,
      notifications,
      setMenuAnchor,
      setNewNotificationExists,
      setNotifications,
      user,
      loadPage,
      setExpanded,
      onSettingsClick,
      enableNotificationTrayV2,
    },
    ref: React.ForwardedRef<HTMLDivElement | null>,
  ) => {
    const { translate } = useTranslation();
    const { classes: styles, cx } = useNotificationListStyles();
    const { notificationClient } = useNotificationClient();

    const showFailedToLoadNotifications = failedToLoad && notifications.length === 0;
    const loadInitializingState =
      !showFailedToLoadNotifications &&
      !noMoreNotifications &&
      (!notifications || notifications.length === 0);
    const elementsLoaded = !showFailedToLoadNotifications && !loadInitializingState;

    const onNotificationReadFromSignalR = useCallback(
      (messageId: string) => {
        setNotifications((currentNotifications) => {
          const newNotifications = currentNotifications.map((currentNotification) => {
            const updatedNotification = structuredClone(currentNotification);
            if (updatedNotification.titleNotification?.notificationId === messageId) {
              updatedNotification.titleNotification.read = true;
            }

            const matchingChildNotification = updatedNotification.children?.find(
              (n) => n.notificationId === messageId,
            );

            if (matchingChildNotification) {
              matchingChildNotification.read = true;
            }

            return updatedNotification;
          });

          return newNotifications;
        });
      },
      [setNotifications],
    );

    const refreshNotifications = useCallback(() => {
      setNewNotificationExists(false);
      setNotifications([]);
      loadPage(true);
    }, [loadPage, setNewNotificationExists, setNotifications]);

    const onAllNotificationsReadFromSignalR = useCallback(() => {
      setNotifications((currentNotifications) => {
        const newNotifications = currentNotifications.map((currentNotification) => {
          const updatedNotification = structuredClone(currentNotification);
          updatedNotification.titleNotification.read = true;

          updatedNotification.children?.forEach((child) => {
            // eslint-disable-next-line no-param-reassign -- NOTE (@mbae, 07/23/24): We already copied the object for mutation
            child.read = true;
          });

          return updatedNotification;
        });

        return newNotifications;
      });
    }, [setNotifications]);

    const markRead = useCallback(
      async (index: number) => {
        if (user?.id == null || index >= notifications.length) {
          return;
        }

        const { ids, newNotifications } = markNotificationAtIndexAsRead(index, notifications);

        try {
          setNotifications(newNotifications);
          const promises = ids.map((id) => {
            if (user.id) {
              const args: CreatorStreamNotificationsMarkCreatorStreamNotificationsReadOperationRequest =
              {
                userId: user.id,
                notificationId: id,
                creatorStreamNotificationsMarkCreatorStreamNotificationsReadRequest: {
                  status: true,
                },
              };
              return notificationClient?.creatorStreamNotificationsMarkCreatorStreamNotificationsRead(
                args,
              );
            }

            return null;
          });

          await Promise.allSettled(promises);
        } catch {
          // (NOTE @mbae 10/01/24): Mark read is best effort
        }
      },
      [notificationClient, notifications, setNotifications, user?.id],
    );

    useEffect(() => {
      signalREventEmitter.on('notificationRead', onNotificationReadFromSignalR);
      signalREventEmitter.on('allNotificationsRead', onAllNotificationsReadFromSignalR);
      return () => {
        signalREventEmitter.removeListener('notificationRead', onNotificationReadFromSignalR);
        signalREventEmitter.removeListener(
          'allNotificationsRead',
          onAllNotificationsReadFromSignalR,
        );
      };
    }, [onAllNotificationsReadFromSignalR, onNotificationReadFromSignalR]);

    useEffect(() => {
      if (listRef.current) {
        const { scrollHeight, clientHeight } = listRef.current;
        const hasScrollbar = scrollHeight > clientHeight;

        if (!hasScrollbar) {
          loadPage(false);
        }
      }
    }, [listRef, loadPage, notifications]);

    useEffect(() => {
      if (!elementsLoaded) {
        return () => {
          // Do nothing
        };
      }

      const handleScroll = () => {
        if (listRef.current) {
          const { scrollHeight, scrollTop, clientHeight } = listRef.current;
          const pixelsFromBottom = scrollHeight - scrollTop - clientHeight;

          if (pixelsFromBottom <= NEXT_PAGE_SCROLL_THRESHOLD) {
            loadPage(false);
          }
        }
      };

      const container = listRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll);
      }

      return () => {
        if (container) {
          container.removeEventListener('scroll', handleScroll);
        }
      };
    }, [elementsLoaded, listRef, loadPage]);

    return (
      <div ref={ref} className={styles.skeleton}>
        <div className={styles.skeletonTray}>
          <div className={styles.outerContainer}>
            <div className={styles.snackbarContainer}>
              <Snackbar
                anchorOrigin={{
                  horizontal: 'center',
                  vertical: 'top',
                }}
                open={newNotificationExists}
                autoHideDuration={6000}
                className={styles.snackbarContainer}
                onClick={refreshNotifications}>
                <div className={styles.snackbarContent}>
                  <Typography variant='body1'>
                    {translate('Action.ViewNewNotifications')}
                  </Typography>
                </div>
              </Snackbar>
            </div>
            <TrayHeader
              notifications={notifications}
              newNotificationExists={newNotificationExists}
              user={user}
              markAllAsRead={markAllAsRead}
              menuAnchor={menuAnchor}
              setMenuAnchor={setMenuAnchor}
              setExpanded={setExpanded}
              onSettingsClick={onSettingsClick}
              enableNotificationTrayV2={enableNotificationTrayV2}
            />
            {showFailedToLoadNotifications && (
              <Grid
                container
                justifyContent='center'
                alignItems='center'
                flexDirection='column'
                spacing={3}
                className={styles.fullCenter}>
                <Grid
                  container
                  justifyContent='center'
                  alignItems='center'
                  flexDirection='column'
                  spacing={0.5}
                  item>
                  <Grid item>
                    <FailedToLoad size={128} />
                  </Grid>
                  <Grid item>
                    <Typography variant='h4'>{translate('Label.NotificationLoadFail')}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant='body2' color='secondary'>
                      {translate('Label.NotificationRetrySuggestion')}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item>
                  <Button color='primary' variant='outlined' onClick={refreshNotifications}>
                    {translate('Action.NotificationRefresh')}
                  </Button>
                </Grid>
              </Grid>
            )}
            {loadInitializingState && (
              <Grid
                container
                justifyContent='center'
                alignItems='center'
                className={styles.fullCenter}>
                <CircularProgress />
              </Grid>
            )}
            {elementsLoaded && notifications.length > 0 && (
              <Grid ref={listRef} className={cx(styles.listContainer, styles.scrollableY)}>
                {notifications.map((notificationGroup: TNotificationGroup, index: number) => {
                  return (
                    <Grid
                      className={styles.notificationContainer}
                      key={`group-${notificationGroup.titleNotification.notificationId}`}>
                      {(notificationGroup.groupingType === 'None' ||
                        notificationGroup.groupingType === 'Summarized') && (
                          enableNotificationTrayV2 ? (
                            <NotificationV2
                              notificationContent={notificationGroup.titleNotification}
                              notificationGroupIndex={index}
                              markRead={markRead}
                            />
                          ) : (
                            <Notification
                              notificationContent={notificationGroup.titleNotification}
                              notificationGroupIndex={index}
                              markRead={markRead}
                            />
                          )
                        )}
                      {notificationGroup.groupingType === 'Bundled' && (
                        <BundledNotification
                          notificationGroup={notificationGroup}
                          notificationGroupIndex={index}
                          markRead={markRead}
                        />
                      )}
                    </Grid>
                  );
                })}
                {loadingTray && (
                  <Grid
                    container
                    justifyContent='center'
                    alignItems='center'
                    className={styles.bottomLoader}>
                    <CircularProgress />
                  </Grid>
                )}
              </Grid>
            )}
            {elementsLoaded && notifications.length === 0 && (
              <Grid
                container
                justifyContent='center'
                alignItems='center'
                flexDirection='column'
                spacing={0.5}
                className={styles.fullCenter}>
                <Grid item>
                  <NoNewNotifications size={128} />
                </Grid>
                <Grid item>
                  <Typography variant='h4' color='secondary'>
                    {translate('Label.NoNotificationsAvailable')}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </div>
        </div>
      </div>
    );
  },
);

NotificationList.displayName = 'NotificationList';

export default NotificationList;
